import React, { useState, useEffect, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom'; 
import { useDispatch, useSelector } from 'react-redux';
import { getAllPatientReminders } from '../../actions/reminderActions';
import { imgUrl } from "../../actions/types";
import { Modal } from 'react-bootstrap'; 
import SelfAppointmentTab from '../doctor/SelfAppointmentTab';
import { getBusinessAddons } from '../../actions/businessAddonActions';
import { updateMedicalRecord } from '../../actions/medicalRecordActions'; 
import MessageModal from "../Pages/MessageModel";

const ReminderTab = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { reminders, loading, error } = useSelector((state) => state.reminderList);
  const { businessAddons } = useSelector((state) => state.businessAddons);

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false); // Addon popup state
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null); 
  const [newFollowUpDate, setNewFollowUpDate] = useState(""); 
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');

  // --- 1. ADDON CHECK LOGIC ---
  const hasSelfAppointmentAddon = businessAddons?.some(
    (addon) => addon.addon_name === "Self Appointment" && addon.status?.toLowerCase() === "active"
  );

  // --- 2. UPDATED HANDLE OPEN FUNCTION (Now with restriction) ---
  const handleOpenAppointment = (patientId) => {
    if (hasSelfAppointmentAddon) {
      setSelectedPatientId(patientId);
      setShowModal(true); 
    } else {
      setShowAddonModal(true); // अगर एडऑन नहीं है तो लॉक वाला पॉपअप दिखाओ
    }
  };

  const handleSaveFollowUpDate = async () => {
    if (!newFollowUpDate) return alert("Please select a date");
    const formData = new FormData();
    formData.append('id', selectedRecord.medical_record_id); 
    formData.append('PatientID', selectedRecord.PatientID);
    formData.append('appointmentId', selectedRecord.appointmentId); 
    formData.append('follow_up_date', newFollowUpDate);
    formData.append('hospitalID', loginData.hospitalID);

    try {
      await dispatch(updateMedicalRecord(formData));
      dispatch(getAllPatientReminders()); 
      setShowDateModal(false);
      alert("Follow-up date updated successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      alert("Error updating date.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPatientId(null);
  };

  // --- 3. COMBINED useEffect (Data fetching) ---
  useEffect(() => {
    dispatch(getAllPatientReminders());
    const hospitalID = localStorage.getItem("hospitalCode");
    if (hospitalID) {
      dispatch(getBusinessAddons({ hospitalID: parseInt(hospitalID) }));
    }
  }, [dispatch]);

  const displayedReminders = useMemo(() => {
    if (!reminders || !Array.isArray(reminders)) return [];
    let filtered = [...reminders];
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(search) || 
        (p.phoneNumber && p.phoneNumber.includes(searchTerm))
      );
    }
    const today = new Date().toISOString().split('T')[0];
    if (dateFilter === "today") {
      filtered = filtered.filter(p => p.latest_follow_up === today);
    } else if (dateFilter === "custom" && customStartDate && customEndDate) {
      filtered = filtered.filter(p => 
        p.latest_follow_up >= customStartDate && p.latest_follow_up <= customEndDate
      );
    }
    return filtered;
  }, [reminders, searchTerm, dateFilter, customStartDate, customEndDate]);

  return (
    <div className="reminder-tab-container">
      {/* 1. Page Header */}
      <div className="page-header filter-bar rounded mb-4">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="page-title">Patient Reminders</h4>
            <ul className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
              <li className="breadcrumb-item active">Reminders</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 2. Filter Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light border-bottom">
          <h4 className="card-title mb-0">Filter Reminders</h4>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 form-group">
              <label className="fw-bold">Search Patient</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Name or Mobile No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label className="d-block fw-bold">Follow-up Date Range</label>
              <div className="d-flex align-items-center flex-wrap gap-3 mt-2">
                {["all", "today", "custom"].map((type) => (
                  <div className="form-check form-check-inline" key={type}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id={type}
                      checked={dateFilter === type}
                      onChange={() => setDateFilter(type)}
                    />
                    <label className="form-check-label text-capitalize" htmlFor={type}>{type}</label>
                  </div>
                ))}
                {dateFilter === "custom" && (
                  <div className="d-flex align-items-center gap-2">
                    <input type="date" className="form-control form-control-sm" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                    <span>to</span>
                    <input type="date" className="form-control form-control-sm" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Status Messages */}
      {loading && <div className="text-center my-5"><div className="spinner-border text-primary"></div><p>Loading...</p></div>}
      {error && <div className="alert alert-danger mx-2">Error: {error}</div>}

      {/* 4. Reminder Cards Grid */}
      <div className="row px-2">
        {!loading && displayedReminders.length > 0 ? (
          displayedReminders.map((patient) => (
            <div className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4 d-flex" key={patient.PatientID}>
              <div className="card appointment-card w-100 shadow-sm border-0 d-flex flex-column">
                <div className="card-body text-center p-3 d-flex flex-column">
                  
                  {/* Patient Image */}
                   <Link to={`/patient-profile/${patient.PatientID}`}>
                    <img 
                      src={patient.uploadPhoto ? `${imgUrl}${patient.uploadPhoto}` : "/assets/img/patient_defaultImage .jpg"} 
                      alt="patient" 
                      className="rounded-circle mb-3 mx-auto" 
                      style={{ width: '85px', height: '85px', objectFit: 'cover', border: '3px solid #f0f0f0', cursor: 'pointer' }}
                      onError={(e) => { e.target.src = "/assets/img/patient_defaultImage .jpg"; }} 
                    />
                  </Link>
                  <Link to={`/patient-profile/${patient.PatientID}`} className="text-decoration-none text-dark">
                    <h5 className="fw-bold mb-1 text-capitalize text-truncate">
                      {patient.firstName} {patient.lastName}
                    </h5>
                  </Link>
                  
                  <div className="mb-2">
                    <span className={`badge ${patient.gender === 'Male' ? 'bg-primary-light' : 'bg-danger-light'}`}>
                       {patient.gender}
                    </span>
                  </div>

                  <div className="text-start small mb-3">
                    <p className="mb-1 text-muted">
                      <i className="fas fa-phone-alt me-2 text-success"></i> {patient.phoneNumber || 'N/A'}
                    </p>
                    <p className="mb-0 text-muted text-truncate" title={`${patient.address}, ${patient.city}`}>
                      <i className="fas fa-map-marker-alt me-2 text-danger"></i> 
                      {patient.city ? `${patient.city}, ${patient.state}` : patient.address || 'Address not added'}
                    </p>
                  </div>
                  
                  <hr className="my-2" />
                  
                  <div className="d-flex justify-content-between px-1 mb-3">
                    <div className="text-start">
                      <small className="text-muted d-block">Total Appointments</small>
                      <span className="fw-bold text-info">{patient.total_appointments}</span>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">Follow-up</small>
                      <div className="d-flex align-items-center justify-content-end">
                        <span className="fw-bold text-danger">{patient.latest_follow_up}</span>
                        <button 
                          className="btn btn-sm text-primary p-0 ms-2" 
                          title="Edit Follow-up Date"
                          onClick={() => {
                            setSelectedRecord(patient); 
                            setNewFollowUpDate(patient.latest_follow_up);
                            setShowDateModal(true);
                          }}
                        >
                          <i className="fas fa-calendar-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Buttons with Addon Restriction Logic */}
                  <div className="mt-auto d-flex flex-column gap-2">
                    <div className="row g-2">
                      <div className="col-6">
                        <a href={`tel:${patient.phoneNumber}`} className="btn btn-success rounded-pill btn-sm w-100">
                          <i className="fas fa-phone me-1"></i> Call
                        </a>
                      </div>
                      <div className="col-6">
                        <a   href={`https://wa.me/${patient.phoneNumber}?text=Hello ${patient.firstName}, this is a reminder for your follow-up visit.`} 
                        target="_blank" rel="noreferrer" className="btn btn-info rounded-pill btn-sm w-100">
                          <i className="fab fa-whatsapp me-1"></i> Chat
                        </a>
                      </div>
                    </div>
                    
                    {/* Add Appointment Button - Now checking Addon */}
                    <button 
                      className="btn btn-info rounded-pill btn-sm w-100" 
                      onClick={() => handleOpenAppointment(patient.PatientID)}
                    >
                    {!hasSelfAppointmentAddon && <i className="fa fa-lock text-warning me-2"></i>}

                      <i className="fas fa-calendar-plus me-1"></i> Add Appointment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : !loading && (
          <div className="col-12 text-center mt-5">
            <p className="text-muted">No patient reminders match your filters.</p>
          </div>
        )}
      </div>

      {/* --- MODALS --- */}

      {/* Booking Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header className='bg-info text-white' closeButton closeVariant="white">
          <Modal.Title>Book Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f8f9fa', maxHeight: '90vh', overflowY: 'auto' }}>
          <SelfAppointmentTab 
            hospitalCode={localStorage.getItem("hospitalCode")} 
            initialPatientId={selectedPatientId} 
          />
        </Modal.Body>
      </Modal>

      {/* ADDON RESTRICTION MODAL */}
      <MessageModal
        show={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        title="Access Restricted"
        variant="warning"
        showConfirm={true}
        confirmText={<span><i className="fas fa-crown me-2"></i> Subscribe Now</span>}
        onConfirm={() => {
          setShowAddonModal(false);
          history.push("/doctor/plan-selection"); 
        }}
        message={
          <div className="alert alert-warning border-0 p-0 mb-0" style={{ background: 'transparent' }}>
            <div className="d-flex align-items-center">
              <i className="fas fa-calendar-check text-warning me-3" style={{ fontSize: '3rem' }}></i>
              <div className="text-start text-dark">
                <strong className="h5 d-block mb-1">Self Appointment Addon Required!</strong>
                <p className="mb-0 small text-muted">Please subscribe to this addon to book appointments directly from reminders.</p>
              </div>
            </div>
          </div>
        }
      />

      {/* Update Follow-up Date Modal */}
      <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered size="sm">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fs-6">Change Follow-up Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-0">
            <label className="small fw-bold">Select New Date:</label>
            <input type="date" className="form-control" value={newFollowUpDate} onChange={(e) => setNewFollowUpDate(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer className="p-2">
          <button className="btn btn-secondary btn-sm" onClick={() => setShowDateModal(false)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSaveFollowUpDate}>Update Date</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReminderTab;